// Dev-only: build device / project preset files and write them straight into the
// repo `assets/` folder (via devAssetWriter). Each device/project gets its OWN folder
// holding its JSON plus its image subfolders, mirrored to the same `_ecranify/*` keys
// the sync uploads:
//   assets/devices/{tier}/<slug>/<slug>.json          + decals/      → _ecranify/devices/<slug>/decals/*
//   assets/projects/{tier}/<slug>/<slug>.json         + backgrounds/ → _ecranify/projects/<slug>/backgrounds/*
//                                                     + decals/      → _ecranify/projects/<slug>/decals/*
// Non-predefined backgrounds/decals are bundled; predefined `_ecranify/*` keys (catalog
// backgrounds, other devices' decals) are kept as-is. Screen images are stripped
// (a preset is public content; the on-device screenshot is per-user).

import type {AppState} from '../../../common/store'
import type {SingleSceneState} from '../../../common/components/store/scenesSlice'
import type {DeviceConfig, SceneFullConfig} from '../../../common/components/types'
import type {CustomDecal} from '../../../common/types/customDecalTypes'
import type {SpriteImage} from '../../../common/types/spriteTypes'
import {DEFAULT_SPRITE_IMAGE_URL} from '../../../common/constants/defaultAssets'
import {writeFileToAssets} from '../../../common/utils/devAssetWriter'
import {serializeScene} from './serializeConfig'
import type {PresetMeta, PresetTier} from './SavePresetDialog'

const PREDEFINED_PREFIX = '_ecranify/'

/** Shape of a projects/{tier}/<slug>/<slug>.json preset file consumed by sync-assets.mjs. */
interface ProjectPresetFile {
  name: string
  description?: string
  tags?: string[]
  isFeatured: boolean
  scenes: {name: string; config: SceneFullConfig}[]
}

const slugifyName = (name: string): string => {
  const slug = name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'preset'
}

// Map an image MIME type to a file extension (defaults to png).
const extFromMime = (mime: string): string => {
  switch (mime) {
    case 'image/jpeg':
      return 'jpg'
    case 'image/webp':
      return 'webp'
    case 'image/svg+xml':
      return 'svg'
    default:
      return 'png'
  }
}

const fetchBlob = async (url: string): Promise<Blob> => (await fetch(url)).blob()

/**
 * Bundle a list of image-bearing items (custom decals OR 2D/3D sprites — anything that
 * extends `SpriteImage`) into the entity's own folder. Three cases per item:
 *  - predefined `_ecranify/*` key  → keep the key (file already in the repo), drop the URL;
 *  - default placeholder (no key, still the static default image) → leave it pointing at
 *    the default so the preset renders the placeholder, not a baked copy;
 *  - real upload (presigned S3 URL or anonymous data URL) → fetch the bytes, write them to
 *    `localDir`, rewrite the key to `keyDir`.
 * Every bundled/predefined item's imageUrl is cleared so the JSON never bakes a temporary URL.
 */
const bundleImageItems = async <T extends SpriteImage>(
  root: FileSystemDirectoryHandle,
  items: T[],
  localDir: string,
  keyDir: string,
  namePrefix: string,
): Promise<T[]> =>
  Promise.all(
    items.map(async (item, i) => {
      if (item.imageKey && item.imageKey.startsWith(PREDEFINED_PREFIX)) {
        return {...item, imageUrl: ''}
      }
      if (!item.imageKey && (!item.imageUrl || item.imageUrl === DEFAULT_SPRITE_IMAGE_URL)) {
        return {...item, imageKey: null, imageUrl: DEFAULT_SPRITE_IMAGE_URL}
      }
      const blob = await fetchBlob(item.imageUrl)
      const filename = `${namePrefix}-${i + 1}.${extFromMime(blob.type)}`
      await writeFileToAssets(root, `${localDir}/${filename}`, blob)
      return {...item, imageKey: `${keyDir}/${filename}`, imageUrl: ''}
    }),
  )

/** Write the active scene's device block to assets/devices/{tier}/<slug>/<slug>.json (+ decals/). */
export const writeDevicePreset = async (
  root: FileSystemDirectoryHandle,
  scene: SingleSceneState,
  deviceConfig: DeviceConfig,
  tier: PresetTier,
): Promise<void> => {
  const {device} = serializeScene(scene, deviceConfig)
  if (!device) return
  const slug = slugifyName(device.name)
  const entityDir = `devices/${tier}/${slug}`
  const deviceOut: DeviceConfig & {customDecals?: CustomDecal[]} = JSON.parse(JSON.stringify(device))
  const decals = scene.customDecals
  if (decals && decals.length > 0) {
    deviceOut.customDecals = await bundleImageItems(
      root,
      decals,
      `${entityDir}/decals`,
      `${PREDEFINED_PREFIX}devices/${slug}/decals`,
      'decal',
    )
  }
  await writeFileToAssets(root, `${entityDir}/${slug}.json`, JSON.stringify(deviceOut, null, 2))
}

/** Write all scenes to assets/projects/{tier}/<slug>/<slug>.json, bundling backgrounds + decals. */
export const writeProjectPreset = async (
  root: FileSystemDirectoryHandle,
  state: AppState,
  deviceConfig: DeviceConfig,
  name: string,
  meta: PresetMeta,
): Promise<void> => {
  const slug = slugifyName(name)
  const entityDir = `projects/${meta.tier}/${slug}`
  const keyBase = `${PREDEFINED_PREFIX}projects/${slug}`
  const scenes = state.scenes.scenes
  const sceneNames = state.scenes.sceneNames
  const outScenes: {name: string; config: SceneFullConfig}[] = []

  for (let i = 0; i < scenes.length; i += 1) {
    // Deep-clone so we never mutate Redux state (serializeScene returns live refs).
    const config = JSON.parse(JSON.stringify(serializeScene(scenes[i]!, deviceConfig))) as SceneFullConfig

    // Strip the per-user screen image — presets are public content.
    delete config.textureKey
    config.textureUrl = ''

    // Bundle a custom background into the project's backgrounds/ folder; predefined
    // `_ecranify/*` (catalog) backgrounds stay as-is.
    const bg = config.background
    if (bg.imageKey && !bg.imageKey.startsWith(PREDEFINED_PREFIX)) {
      const blob = await fetchBlob(scenes[i]!.bgImageUrl)
      const filename = `s${i + 1}-bg.${extFromMime(blob.type)}`
      await writeFileToAssets(root, `${entityDir}/backgrounds/${filename}`, blob)
      bg.imageKey = `${keyBase}/backgrounds/${filename}`
      bg.imageUrl = ''
    }

    if (config.customDecals && config.customDecals.length > 0) {
      config.customDecals = await bundleImageItems(
        root,
        config.customDecals,
        `${entityDir}/decals`,
        `${keyBase}/decals`,
        `s${i + 1}-decal`,
      )
    }

    // Bundle 2D + 3D sprites into the project's sprites/ folder (same rules as decals).
    if (config.sprites2d && config.sprites2d.length > 0) {
      config.sprites2d = await bundleImageItems(
        root,
        config.sprites2d,
        `${entityDir}/sprites`,
        `${keyBase}/sprites`,
        `s${i + 1}-sprite2d`,
      )
    }
    if (config.sprites3d && config.sprites3d.length > 0) {
      config.sprites3d = await bundleImageItems(
        root,
        config.sprites3d,
        `${entityDir}/sprites`,
        `${keyBase}/sprites`,
        `s${i + 1}-sprite3d`,
      )
    }

    outScenes.push({name: sceneNames[i] ?? `Scene ${i + 1}`, config})
  }

  const file: ProjectPresetFile = {
    name,
    ...(meta.description && {description: meta.description}),
    ...(meta.tags && meta.tags.length > 0 && {tags: meta.tags}),
    isFeatured: meta.featured ?? true,
    scenes: outScenes,
  }
  await writeFileToAssets(root, `${entityDir}/${slug}.json`, JSON.stringify(file, null, 2))
}
